import { useEffect, useState } from 'react';
import { CheckSquare, Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { devLog } from '../utils/logger';

const TASK_TYPES = [
  { id: 'opening', title: 'Opening Tasks', placeholder: 'Add opening task...' },
  { id: 'closing', title: 'Closing Tasks', placeholder: 'Add closing task...' },
];

export default function ServiceTasksPanel() {
  const [tasks, setTasks] = useState({ opening: [], closing: [] });
  const [forms, setForms] = useState({ opening: '', closing: '' });
  const [saving, setSaving] = useState('');

  const fetchTasks = async () => {
    try {
      const { data } = await api.get('/settings/service-tasks');
      setTasks(data);
    } catch (err) {
      devLog.error('Service tasks fetch failed:', err);
    }
  };

  useEffect(() => { fetchTasks(); }, []);

  const addTask = async (type) => {
    const text = forms[type].trim();
    if (!text) return;
    setSaving(type);
    try {
      const { data } = await api.post(`/settings/service-tasks/${type}`, { text });
      setTasks(data);
      setForms(f => ({ ...f, [type]: '' }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not save task');
    } finally {
      setSaving('');
    }
  };

  const deleteTask = async (type, taskId) => {
    setSaving(taskId);
    try {
      const { data } = await api.delete(`/settings/service-tasks/${type}/${taskId}`);
      setTasks(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete task');
    } finally {
      setSaving('');
    }
  };

  return (
    <div className="space-y-4" data-testid="service-tasks-panel">
      {TASK_TYPES.map(section => (
        <div key={section.id} className="rounded-3xl border border-[#7D8B69]/15 bg-white p-4 shadow-[0_8px_25px_rgba(27,58,26,0.05)] sm:p-5" data-testid={`${section.id}-tasks-section`}>
          <h3 className="mb-3 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-wider text-[#4A5D4E]">
            <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#E8F0E4] text-[#1B3A1A]"><CheckSquare size={14} /></span> {section.title}
          </h3>
          <div className="space-y-2">
            {tasks[section.id]?.map(task => (
              <div key={task.id} className="flex items-center gap-3 rounded-2xl border border-[#7D8B69]/10 bg-[#FDFBF7] px-4 py-3 transition-all duration-200 hover:bg-[#F3F0E6]" data-testid={`${section.id}-task-${task.id}`}>
                <span className="flex-1 font-mono text-sm text-[#1B3A1A]">{task.text}</span>
                <button
                  onClick={() => deleteTask(section.id, task.id)}
                  disabled={saving === task.id}
                  className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#F4B4B4] text-[#C43030] transition-all duration-200 hover:bg-[#FFF3F3] active:scale-95 disabled:opacity-40"
                  data-testid={`delete-${section.id}-task-${task.id}`}
                  aria-label={`Delete ${section.id} task`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {tasks[section.id]?.length === 0 && (
              <div className="rounded-2xl border border-dashed border-[#7D8B69]/25 bg-[#FDFBF7] py-5 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]" data-testid={`${section.id}-tasks-empty`}>
                No {section.id} tasks yet
              </div>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              value={forms[section.id]}
              onChange={e => setForms(f => ({ ...f, [section.id]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') addTask(section.id); }}
              placeholder={section.placeholder}
              className="h-12 flex-1 rounded-xl border border-[#7D8B69]/25 bg-[#FDFBF7] px-4 text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10"
              data-testid={`${section.id}-task-input`}
            />
            <button
              onClick={() => addTask(section.id)}
              disabled={saving === section.id || !forms[section.id].trim()}
              className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1B3A1A] text-white transition-all duration-200 hover:bg-[#2C4C2B] active:scale-95 disabled:opacity-40"
              data-testid={`add-${section.id}-task-btn`}
              aria-label={`Add ${section.id} task`}
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

